/* eslint-disable no-console */
import { GraphQLFormattedError } from 'graphql';

import { BASE_PATH } from '@/constants/base-path';

import { type SEVERITY, SEVERITY_ERROR, SEVERITY_INFO, SEVERITY_WARN } from './logSeverity';

const LOG_URL = `${BASE_PATH}/api/log`;
const RETRY_SEQUENCE = [10, 20, 40];

// eslint-disable-next-line no-unused-vars
type LogFunction = (severity: SEVERITY, message: string | GraphQLFormattedError) => void;

interface ServerLogger {
  log: LogFunction;
}

let serverLogger: ServerLogger | null = null;

export const setupServerLogger = (logger: ServerLogger): void => {
  serverLogger = logger;
};

export const getServerLogger = () => serverLogger;

const sleep = async (ms: number) =>
  new Promise((r) => {
    setTimeout(r, ms);
  });

const fetchRetry = async (url: string, options = {}, retries = RETRY_SEQUENCE): Promise<Response> => {
  try {
    const result = await fetch(url, options);
    if (result.ok) {
      return result;
    }
  } catch (error) {
    if (error instanceof Error) {
      console.error('Error not logged', error.message, '- retrying...');
    }
  }

  if (retries?.length) {
    const [timeout, ...restRetries] = retries;
    await sleep(timeout * 1000);

    return fetchRetry(url, options, restRetries);
  }
  throw new Error('Error not logged');
};

const log: LogFunction = (severity, message) => {
  // eslint-disable-next-line no-console
  if (severity === SEVERITY_ERROR) {
    console.error(`[${severity}]: ${message}`);
  } else if (severity === SEVERITY_WARN) {
    console.warn(`[${severity}]: ${message}`);
  } else {
    console.info(`[${severity}]: ${message}`);
  }

  if (serverLogger) {
    try {
      serverLogger.log(severity, `[SSR]: ${message}`);
    } catch (error) {
      if (error instanceof Error) {
        console.error(error.message);
      }
    }
  } else {
    try {
      fetchRetry(LOG_URL, { method: 'POST', body: JSON.stringify([{ severity, message }]) });
    } catch {
      console.error('Cannot log error due to network error');
    }
  }
};

export const logInfo = (message: string) => {
  log(SEVERITY_INFO, message);
};

export const logWarn = (message: string) => {
  log(SEVERITY_WARN, message);
};

export const logError = (message: string | GraphQLFormattedError) => {
  log(SEVERITY_ERROR, message);
};
